import { createTheme, responsiveFontSizes } from '@mui/material/styles';
import { hexToPastel } from './components/utils/commons';

// ─────────────────────────────────────────────────────────────────────────────
// Single source of truth for every hex value used across the web app.
// The MUI theme below is built from these tokens, and non-MUI consumers
// (react-flow strokes, `sx` string fills, canvas gradients, etc.) should import
// `colors` from here instead of hardcoding hex.
// Add new colors HERE — never inline them in components.
// ─────────────────────────────────────────────────────────────────────────────
export const colors = {
  // Brand & status (also exposed as MUI palette roles below)
  primary: '#1a242d',
  secondary: '#FF0266',
  success: '#46bf83',
  info: '#0070B7',
  warning: '#ffa400',
  error: '#fe2d38',

  // Base
  white: '#ffffff',
  black: '#000000',

  // Tailwind-style hue scale (existing palette roles)
  slate: '#64748b',
  gray: '#6b7280',
  zinc: '#71717a',
  neutral: '#737373',
  stone: '#78716c',
  red: '#ef4444',
  orange: '#f97316',
  amber: '#f59e0b',
  yellow: '#eab308',
  lime: '#84cc16',
  green: '#22c55e',
  emerald: '#10b981',
  teal: '#14b8a6',
  cyan: '#06b6d4',
  sky: '#0ea5e9',
  blue: '#3b82f6',
  indigo: '#6366f1',
  violet: '#8b5cf6',
  purple: '#a855f7',
  fuchsia: '#d946ef',
  pink: '#ec4899',
  rose: '#f43f5e',

  // Seeds for the pastel palette variants
  pastelYellowSeed: '#fde047',
  pastelSkySeed: '#0ea5e9',
  pastelEmeraldSeed: '#10b981',
  pastelVioletSeed: '#8b5cf6',

  // ── Neutrals / text / surfaces (added from pipeline-monitoring views) ──
  slate600: '#475569', // secondary text
  slate400: '#94a3b8', // muted text / neutral edge stroke
  slate200: '#e2e8f0', // light border
  slate100: '#f1f5f9', // sunken surface (one step darker than slate50)
  slate50: '#f8fafc', // muted surface
  textGray: '#5f5f5f', // muted body text
  grayMuted: '#8e8e93', // disabled / secondary (iOS systemGray)
  slateMuted: '#7c909b', // muted slate (progress labels)
  surfaceGray: '#f2f2f7', // light app surface
  surfaceMutedBlue: '#edf0f3', // progress track background
  lavender: '#e9e9f1', // light divider / border
  surfaceLavender: '#f7f6fa', // light lavender surface
  lavenderTint: '#cacadd57', // translucent lavender overlay (8-digit, incl. alpha)

  // ── Status accents ──
  statusNoPolicies: '#f9d949', // "no alert policy" yellow
  danger: '#fa5f55', // alert red (graph / chips)
  limeBright: '#bfd200',
  warningSurface: '#fef7ea', // light amber background
  warningAccent: '#ffd95a',
  warningAccentDark: '#e0bf53',
  violetAccent: '#9171d6',
  blueAccent: '#5b86d6',

  // ── Emerald / green progress scale (MultiProgressBar) ──
  emerald900: '#065f46',
  emerald800: '#047857',
  emerald700: '#059669',
  emerald400: '#34d399',
  emerald300: '#6ee7b7',
  emerald200: '#a7f3d0',
  teal300: '#5eead4',
  green300: '#86efac',
  green200: '#bbf7d0',

  // ── Dark info-drawer surfaces ──
  drawerSurface: '#1a252c',
  drawerSurfaceAlt: '#29373f',
  drawerBorder: '#33444d',
  drawerText: '#e9eef1',
  drawerTextMuted: '#9fb0b9',
};

let theme = createTheme({
  // Theme customization goes here as usual, including tonalOffset and/or
  // contrastThreshold as the augmentColor() function relies on these
});

theme = createTheme({
  palette: {
    type: 'light',
    primary: theme.palette.augmentColor({
      color: { main: colors.primary },
      name: 'primary',
    }),
    secondary: theme.palette.augmentColor({
      color: { main: colors.secondary },
      name: 'secondary',
    }),
    success: theme.palette.augmentColor({
      color: { main: colors.success },
      name: 'success',
    }),
    info: theme.palette.augmentColor({
      color: { main: colors.info },
      name: 'info',
    }),
    warning: theme.palette.augmentColor({
      color: { main: colors.warning },
      name: 'warning',
    }),
    error: theme.palette.augmentColor({
      color: { main: colors.error },
      name: 'error',
    }),
    slate: theme.palette.augmentColor({
      color: { main: colors.slate },
      name: 'slate',
    }),
    gray: theme.palette.augmentColor({
      color: { main: colors.gray },
      name: 'gray',
    }),
    zinc: theme.palette.augmentColor({
      color: { main: colors.zinc },
      name: 'zinc',
    }),
    neutral: theme.palette.augmentColor({
      color: { main: colors.neutral },
      name: 'neutral',
    }),
    stone: theme.palette.augmentColor({
      color: { main: colors.stone },
      name: 'stone',
    }),
    red: theme.palette.augmentColor({
      color: { main: colors.red },
      name: 'red',
    }),
    orange: theme.palette.augmentColor({
      color: { main: colors.orange },
      name: 'orange',
    }),
    amber: theme.palette.augmentColor({
      color: { main: colors.amber },
      name: 'amber',
    }),
    yellow: theme.palette.augmentColor({
      color: { main: colors.yellow },
      name: 'yellow',
    }),
    lime: theme.palette.augmentColor({
      color: { main: colors.lime },
      name: 'lime',
    }),
    green: theme.palette.augmentColor({
      color: { main: colors.green },
      name: 'green',
    }),
    emerald: theme.palette.augmentColor({
      color: { main: colors.emerald },
      name: 'emerald',
    }),
    teal: theme.palette.augmentColor({
      color: { main: colors.teal },
      name: 'teal',
    }),
    cyan: theme.palette.augmentColor({
      color: { main: colors.cyan },
      name: 'cyan',
    }),
    sky: theme.palette.augmentColor({
      color: { main: colors.sky },
      name: 'sky',
    }),
    blue: theme.palette.augmentColor({
      color: { main: colors.blue },
      name: 'blue',
    }),
    indigo: theme.palette.augmentColor({
      color: { main: colors.indigo },
      name: 'indigo',
    }),
    violet: theme.palette.augmentColor({
      color: { main: colors.violet },
      name: 'violet',
    }),
    purple: theme.palette.augmentColor({
      color: { main: colors.purple },
      name: 'purple',
    }),
    fuchsia: theme.palette.augmentColor({
      color: { main: colors.fuchsia },
      name: 'fuchsia',
    }),
    pink: theme.palette.augmentColor({
      color: { main: colors.pink },
      name: 'pink',
    }),
    rose: theme.palette.augmentColor({
      color: { main: colors.rose },
      name: 'rose',
    }),
    pastelYellow: theme.palette.augmentColor({
      color: { main: hexToPastel(colors.pastelYellowSeed) },
      name: 'pastelYellow',
    }),
    pastelSky: theme.palette.augmentColor({
      color: { main: hexToPastel(colors.pastelSkySeed) },
      name: 'pastelSky',
    }),
    pastelEmerald: theme.palette.augmentColor({
      color: { main: hexToPastel(colors.pastelEmeraldSeed) },
      name: 'pastelEmerald',
    }),
    pastelViolet: theme.palette.augmentColor({
      color: { main: hexToPastel(colors.pastelVioletSeed) },
      name: 'pastelViolet',
    }),
  },
});

theme = responsiveFontSizes(theme);

export default theme;
